import { costedLines, lineCost, procedureProfit, roundMinor } from "@dentacore/shared-types";
import type {
  AddProcedureMaterial,
  CostedLine,
  EditProcedureMaterial,
  InventoryItem,
  ProcedureCode,
  ProcedureMaterial,
} from "@dentacore/shared-types";

import { AddLine, amountText, EditLine, LineName } from "../inventory/tray-panel";
import { formatMinor } from "./procedure-form";
import styles from "./procedures.module.css";

/**
 * What a procedure uses, what that costs, and what is left of its price —
 * STORY-175b, decisions MC-D1, MC-D2, MC-D6 and MC-D7.
 *
 * **It is the procedure's tray, not a second list** (MC-D1). The rows are
 * `procedure_materials`, the same ones Inventory → Procedure trays shows, and
 * editing reuses the tray's own controls under the tray's own permission
 * (`procedures.manage`). Read first and edited on purpose, as the tray is (IV7).
 *
 * **Money only for people who can see Accounting** (MC-D7). Everyone else reads
 * the lines and amounts. **The server enforces it too since QF-D1** (2026-10-17):
 * without Accounting and `accounting.view` the lines arrive with no price, so the
 * panel costs only what `costedLines` says it can, and draws no money otherwise.
 *
 * **One sitting on one tooth** (MC-D6): a per-tooth line counts once, and the
 * note under the figures says so rather than the panel guessing a tooth count.
 * The cost rule itself is shared-types' `procedureProfit` — never redone here.
 */
export function ProcedureMaterials({
  code,
  currency,
  materials,
  items,
  canEdit,
  canSeeMoney,
  busy,
  editing,
  onEditing,
  onAdd,
  onEdit,
  onRemove,
}: {
  code: ProcedureCode;
  currency: string;
  /** Null while it is being read. */
  materials: readonly ProcedureMaterial[] | null;
  /** This location's stock to add from — read only once editing starts. */
  items: readonly InventoryItem[] | null;
  canEdit: boolean;
  canSeeMoney: boolean;
  busy: boolean;
  editing: boolean;
  onEditing: (editing: boolean) => void;
  onAdd: (body: AddProcedureMaterial) => Promise<boolean>;
  onEdit: (materialId: string, body: EditProcedureMaterial) => void;
  onRemove: (materialId: string, name: string) => void;
}): React.JSX.Element {
  const edit = editing && canEdit && code.isActive;
  const lines = materials ?? [];
  const money = (minor: number): string => formatMinor(minor, currency);
  // Null when this person may not see money, or the server sent none (QF-D1).
  const costed = canSeeMoney ? costedLines(lines) : null;

  return (
    <section className={styles.materials} aria-label="Materials" data-testid="proc-materials">
      <div className={styles.materialsHead}>
        <h3 className={styles.materialsTitle}>Materials</h3>
        {canEdit &&
          code.isActive &&
          (edit ? (
            <button
              type="button"
              className={styles.primary}
              onClick={() => onEditing(false)}
              data-testid="proc-materials-done"
            >
              Done
            </button>
          ) : (
            <button
              type="button"
              className={styles.ghost}
              onClick={() => onEditing(true)}
              data-testid="proc-materials-edit"
            >
              Edit materials
            </button>
          ))}
      </div>

      {materials === null ? (
        <p className={styles.materialsNote}>Reading&hellip;</p>
      ) : lines.length === 0 ? (
        <p className={styles.materialsNote} data-testid="proc-materials-empty">
          Nothing listed yet{costed !== null ? ", so the whole price counts as profit." : "."}
        </p>
      ) : (
        <div className={styles.materialLines}>
          {lines.map((line, index) =>
            edit ? (
              <EditLine
                key={line.id}
                line={line}
                busy={busy}
                onEdit={(body) => onEdit(line.id, body)}
                onRemove={() => onRemove(line.id, line.itemName)}
              />
            ) : (
              <div key={line.id} className={styles.materialLine} data-testid="proc-material-line">
                <LineName line={line} />
                <span className={styles.materialAmount}>{amountText(line)}</span>
                {costed?.[index] !== undefined && (
                  <span className={styles.materialCost} data-testid="proc-material-cost">
                    {line.itemKind === "REUSABLE"
                      ? "—"
                      : money(roundMinor(lineCost(costed[index])))}
                  </span>
                )}
              </div>
            ),
          )}
        </div>
      )}

      {edit && <AddLine codeId={code.id} lines={lines} items={items} busy={busy} onAdd={onAdd} />}

      {costed !== null && materials !== null && <Profit code={code} lines={costed} money={money} />}
    </section>
  );
}

/** Price · Materials · Profit, for one sitting on one tooth (MC-D2, MC-D6). */
function Profit({
  code,
  lines,
  money,
}: {
  code: ProcedureCode;
  lines: readonly (ProcedureMaterial & CostedLine)[];
  money: (minor: number) => string;
}): React.JSX.Element {
  const { materialsCents, profitCents } = procedureProfit(code.defaultPriceCents, lines);
  const perTooth = lines.some((line) => line.scope === "PER_TOOTH");
  const many = code.visits.length > 0;

  return (
    <div className={styles.profit} data-testid="proc-profit">
      <dl className={styles.profitRows}>
        <div className={styles.profitRow}>
          <dt>Price</dt>
          <dd data-testid="proc-profit-price">{money(code.defaultPriceCents)}</dd>
        </div>
        <div className={styles.profitRow}>
          <dt>Materials</dt>
          <dd data-testid="proc-profit-materials">− {money(materialsCents)}</dd>
        </div>
        <div className={`${styles.profitRow} ${styles.profitTotal}`}>
          <dt>Profit</dt>
          <dd
            className={profitCents < 0 ? styles.profitLoss : undefined}
            data-testid="proc-profit-value"
          >
            {money(profitCents)}
          </dd>
        </div>
      </dl>
      {(perTooth || many) && (
        <p className={styles.profitNote}>
          {[
            many ? "All visits together." : null,
            perTooth ? "Per-tooth materials counted for one tooth." : null,
          ]
            .filter(Boolean)
            .join(" ")}
        </p>
      )}
    </div>
  );
}
